import React, { useRef, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  FormControl,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { CheckCircle } from '@mui/icons-material'
import { useAtom, useAtomValue } from 'infrastructure/state/jotai.wrapper'
import {
  customFormatFileNameAtom,
  gDriveFilesForTree,
  gradeStringAtom,
  nodeTitleAtom,
} from './atoms'
import { detectFilenameFormat } from './detectFilenameFormat'

export function Third() {
  return (
    <Box>
      <Box>
        <strong>Third</strong>
        <Typography variant="body1" mb={1}>
          Choose how to match the unit/lesson/activity to the
          folders/presentations
        </Typography>
      </Box>
      <FileName />
      <NodeTitle />
    </Box>
  )
}

function PartsComposer({
  current,
  onChange,
}: {
  current: string
  onChange: (s: string) => void
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const formatParts = ['grade', 'module', 'unit', 'lesson', 'character', 'name']

  const insertComponent = (textToInsert: string) => {
    const input = inputRef.current
    if (!input) return
    const start = input.selectionStart ?? 0
    const end = input.selectionEnd ?? 0
    const newValue = current.slice(0, start) + textToInsert + current.slice(end)
    onChange(newValue)
    setTimeout(() => {
      input.focus()
      input.setSelectionRange(
        start + textToInsert.length,
        start + textToInsert.length,
      )
    }, 0)
  }

  return (
    <>
      <Box>
        {formatParts.map((part) => (
          <Chip
            key={part}
            label={`{${part}}`}
            onClick={() => insertComponent(`{${part}}`)}
          />
        ))}
      </Box>
      <TextField
        fullWidth
        value={current}
        inputRef={inputRef}
        onChange={(e) => onChange(e.target.value)}
        variant={'standard'}
      />
    </>
  )
}

function FileName() {
  const [customFormatFileName, setCustomFormatFileName] = useAtom(
    customFormatFileNameAtom,
  )
  const gDriveFiles = useAtomValue(gDriveFilesForTree)
  const gradeString = useAtomValue(gradeStringAtom)
  const [detectResult, setDetectResult] = useState<string | null>(null)

  const handleAutoDetect = () => {
    if (!gradeString || gDriveFiles.length === 0) return
    const fileNames = gDriveFiles.map((f) => f.name)
    const result = detectFilenameFormat(fileNames, gradeString)
    if (result) {
      setCustomFormatFileName(result.format)
      setDetectResult(`Matched ${result.matchCount}/${result.totalFiles} files`)
    } else {
      setDetectResult('Could not detect format')
    }
  }

  return (
    <Box sx={{ mb: 2 }}>
      <FormControl>
        <Typography mb={1}>
          Use to create a custom format for the file name to match the lesson
        </Typography>
        <PartsComposer
          current={customFormatFileName}
          onChange={setCustomFormatFileName}
        />
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            disabled={gDriveFiles.length === 0}
            onClick={handleAutoDetect}
          >
            Auto-detect format
          </Button>
          {detectResult && (
            <Typography variant="body2" color="text.secondary">
              {detectResult}
            </Typography>
          )}
        </Box>
      </FormControl>
    </Box>
  )
}

function NodeTitle() {
  const [nodeTitle, setNodeTitle] = useAtom(nodeTitleAtom)
  return (
    <Box sx={{ mb: 2 }}>
      <Typography mb={1}>Node title to search for</Typography>
      <Stack direction="row" spacing={2}>
        <Box>
          <TextField
            id="setNodeTitle"
            onChange={(e) => {
              setNodeTitle(e.target.value)
            }}
            value={nodeTitle}
            variant="standard"
          ></TextField>
          <CheckCircle color={nodeTitle !== '' ? 'success' : 'disabled'} />
        </Box>
      </Stack>
    </Box>
  )
}
